import { supabase } from '../lib/supabase';

/**
 * Analytics Service
 * Provides real-time analytics data for PCF Admin Dashboard
 */

/**
 * Get total number of users (all time)
 * @returns {Promise<number>} Count of all users
 */
export const getTotalUsers = async () => {
  try {
    // Count all authorized users from role_email_authorizations
    const { count, error } = await supabase
      ?.from('role_email_authorizations')
      ?.select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching total users:', error);
    throw error;
  }
};

/**
 * Get number of active users (users who completed registration)
 * @returns {Promise<number>} Count of active users
 */
export const getActiveUsers = async () => {
  try {
    // Count users who have created accounts (user_profiles)
    const { count, error } = await supabase
      ?.from('user_profiles')
      ?.select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching active users:', error);
    throw error;
  }
};

/**
 * Get total number of teams formed
 * @returns {Promise<number>} Count of teams
 */
export const getTotalTeamsFormed = async () => {
  try {
    const { count, error } = await supabase?.from('teams')?.select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching teams count:', error);
    throw error;
  }
};

/**
 * Get total deliverables submitted across all stages
 * @returns {Promise<number>} Count of deliverables
 */
export const getTotalDeliverablesSubmitted = async () => {
  try {
    // FIXED: Count from team_deliverable_uploads to include all student file uploads
    const { count, error } = await supabase?.from('team_deliverable_uploads')?.select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching deliverables count:', error);
    throw error;
  }
};

/**
 * Get total meetings scheduled
 * @returns {Promise<number>} Count of meetings
 */
export const getTotalMeetingsScheduled = async () => {
  try {
    const { count, error } = await supabase?.from('meetings')?.select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching meetings count:', error);
    throw error;
  }
};

/**
 * Get number of unique cities participating
 * @returns {Promise<number>} Count of unique cities
 */
export const getCitiesParticipating = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('location')?.not('location', 'is', null);

    if (error) throw error;

    // Extract unique cities (assuming location format includes city)
    const uniqueCities = new Set(
      data?.map(profile => {
        // Extract city from location string (e.g., "San Francisco, CA" -> "San Francisco")
        const city = profile?.location?.split(',')[0]?.trim();
        return city;
      }).filter(Boolean)
    );

    return uniqueCities?.size;
  } catch (error) {
    console.error('Error fetching cities count:', error);
    throw error;
  }
};

/**
 * Get total files uploaded across all stages
 * @returns {Promise<number>} Count of files
 */
export const getTotalFilesUploaded = async () => {
  try {
    // Count the number of upload records (each record represents one file)
    const { count, error } = await supabase?.from('team_stage_uploads')?.select('*', { count: 'exact', head: true });

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error fetching files count:', error);
    throw error;
  }
};

/**
 * Get weekly team login metrics (7-day period)
 * Counts teams where at least one member logged in within the last 7 days
 * Note: Currently using created_at as proxy for activity since last_sign_in_at doesn't exist
 * @returns {Promise<{activeTeams: number, totalTeams: number, percentage: number}>}
 */
export const getWeeklyTeamLogins = async () => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);

    // Get all teams
    const { data: allTeams, error: teamsError } = await supabase?.from('teams')?.select('id');
    if (teamsError) throw teamsError;

    const totalTeams = allTeams?.length || 0;

    if (totalTeams === 0) {
      return { activeTeams: 0, totalTeams: 0, percentage: 0 };
    }

    // Get teams with at least one member who created account or was active in the last 7 days
    // Using created_at as a proxy for activity until last_sign_in_at is added to schema
    const { data: activeTeamMembers, error: membersError } = await supabase
      ?.from('user_profiles')
      ?.select('team_id, created_at')
      ?.not('team_id', 'is', null)
      ?.gte('created_at', sevenDaysAgo?.toISOString());

    if (membersError) throw membersError;

    // Get unique team IDs (team-level counting)
    const activeTeamIds = new Set(activeTeamMembers?.map(member => member?.team_id));
    const activeTeams = activeTeamIds?.size;

    let percentage = Math.round((activeTeams / totalTeams) * 100);

    return { activeTeams, totalTeams, percentage };
  } catch (error) {
    console.error('Error fetching weekly team logins:', error);
    throw error;
  }
};

/**
 * Get weekly team chat activity (7-day period)
 * Counts teams where at least one member sent a message in the last 7 days
 * @returns {Promise<{activeTeams: number, totalTeams: number, percentage: number}>}
 */
export const getWeeklyTeamChatActivity = async () => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);

    // Get all teams
    const { data: allTeams, error: teamsError } = await supabase?.from('teams')?.select('id');
    if (teamsError) throw teamsError;

    const totalTeams = allTeams?.length || 0;

    if (totalTeams === 0) {
      return { activeTeams: 0, totalTeams: 0, percentage: 0 };
    }

    // Get team conversations
    const { data: teamConversations, error: convError } = await supabase
      ?.from('conversations')
      ?.select('id, team_id')
      ?.not('team_id', 'is', null);

    if (convError) throw convError;

    if (!teamConversations || teamConversations?.length === 0) {
      return { activeTeams: 0, totalTeams, percentage: 0 };
    }

    const conversationIds = teamConversations?.map(conv => conv?.id);
    const teamIdsByConversation = {};
    teamConversations?.forEach(conv => {
      teamIdsByConversation[conv?.id] = conv?.team_id;
    });

    // Get messages sent in the last 7 days in team conversations
    const { data: recentMessages, error: messagesError } = await supabase
      ?.from('messages')
      ?.select('conversation_id')
      ?.in('conversation_id', conversationIds)
      ?.gte('created_at', sevenDaysAgo?.toISOString());

    if (messagesError) throw messagesError;

    // Get unique team IDs that have chat activity
    const activeTeamIds = new Set(
      recentMessages?.map(message => teamIdsByConversation[message?.conversation_id])?.filter(Boolean)
    );

    const activeTeams = activeTeamIds?.size;
    let percentage = Math.round((activeTeams / totalTeams) * 100);

    return { activeTeams, totalTeams, percentage };
  } catch (error) {
    console.error('Error fetching weekly team chat activity:', error);
    throw error;
  }
};

/**
 * Get teams at risk based on multiple criteria
 * @returns {Promise<Array>} Array of at-risk teams with details
 */
export const getTeamsAtRisk = async () => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);

    // Get all teams with their members and activity using correct foreign key relationship
    const { data: teams, error: teamsError } = await supabase?.from('teams')?.select(`
        id,
        team_name,
        team_number,
        user_profiles!user_profiles_team_id_fkey (
          id,
          full_name,
          created_at
        )
      `);

    if (teamsError) throw teamsError;

    const atRiskTeams = [];

    for (const team of teams || []) {
      const riskReasons = [];

      // Check 1: No logins in 7 days
      const hasRecentLogin = team?.user_profiles?.some(profile => {
        const lastLogin = new Date(profile?.created_at);
        return lastLogin >= sevenDaysAgo;
      });

      if (!hasRecentLogin) {
        riskReasons?.push('No logins in 7 days');
      }

      // Check 2: 3+ overdue tasks
      const { data: overdueTasks } = await supabase?.from('team_tasks')?.select('id')?.eq('team_id', team?.id)?.eq('status', 'pending')?.lt('due_date', new Date()?.toISOString());

      if (overdueTasks?.length >= 3) {
        riskReasons?.push(`${overdueTasks?.length} overdue tasks`);
      }

      // Check 3: No deliverables uploaded
      const { count: deliverablesCount } = await supabase?.from('team_stage_uploads')?.select('*', { count: 'exact', head: true })?.eq('team_id', team?.id);

      if (!deliverablesCount || deliverablesCount === 0) {
        riskReasons?.push('No deliverables uploaded');
      }

      // Check 4: No messages in chat
      const { data: conversations } = await supabase?.from('conversations')?.select('id')?.eq('team_id', team?.id);

      if (conversations && conversations?.length > 0) {
        const conversationIds = conversations?.map(c => c?.id);
        const { count: messagesCount } = await supabase?.from('messages')?.select('*', { count: 'exact', head: true })?.in('conversation_id', conversationIds);

        if (!messagesCount || messagesCount === 0) {
          riskReasons?.push('No messages in chat');
        }
      } else {
        riskReasons?.push('No chat conversation created');
      }

      // Add to at-risk list if any criteria met
      if (riskReasons?.length > 0) {
        atRiskTeams?.push({
          teamId: team?.id,
          teamName: team?.team_name,
          teamNumber: team?.team_number,
          riskReasons,
          riskLevel: riskReasons?.length >= 3 ? 'high' : riskReasons?.length >= 2 ? 'medium' : 'low'
        });
      }
    }

    return atRiskTeams;
  } catch (error) {
    console.error('Error fetching at-risk teams:', error);
    throw error;
  }
};

/**
 * Get 7-day change percentage for a metric
 * Compares last 7 days vs previous 7 days based on admin's last login
 * @param {string} metric - Metric type: 'users', 'teams', 'deliverables', 'cities'
 * @param {Date} adminLastLogin - Admin's last login timestamp
 * @returns {Promise<{current: number, previous: number, percentage: number, changeType: string}>}
 */
export const get7DayChange = async (metric, adminLastLogin = new Date()) => {
  try {
    const lastLoginDate = new Date(adminLastLogin);
    const last7DaysStart = new Date(lastLoginDate);
    last7DaysStart?.setDate(last7DaysStart?.getDate() - 7);
    
    const previous7DaysStart = new Date(last7DaysStart);
    previous7DaysStart?.setDate(previous7DaysStart?.getDate() - 7);

    let currentValue = 0;
    let previousValue = 0;

    switch (metric) {
      case 'users': {
        // Count role_email_authorizations created in last 7 days
        const { count: currentCount } = await supabase?.from('role_email_authorizations')?.select('*', { count: 'exact', head: true })?.gte('created_at', last7DaysStart?.toISOString())?.lte('created_at', lastLoginDate?.toISOString());
        
        const { count: previousCount } = await supabase?.from('role_email_authorizations')?.select('*', { count: 'exact', head: true })?.gte('created_at', previous7DaysStart?.toISOString())?.lt('created_at', last7DaysStart?.toISOString());
        
        currentValue = currentCount || 0;
        previousValue = previousCount || 0;
        break;
      }
      
      case 'teams': {
        // Count teams created in last 7 days
        const { count: currentCount } = await supabase?.from('teams')?.select('*', { count: 'exact', head: true })?.gte('created_at', last7DaysStart?.toISOString())?.lte('created_at', lastLoginDate?.toISOString());
        
        const { count: previousCount } = await supabase?.from('teams')?.select('*', { count: 'exact', head: true })?.gte('created_at', previous7DaysStart?.toISOString())?.lt('created_at', last7DaysStart?.toISOString());
        
        currentValue = currentCount || 0;
        previousValue = previousCount || 0;
        break;
      }
      
      case 'deliverables': {
        // Count deliverables uploaded in last 7 days
        const { count: currentCount } = await supabase?.from('team_stage_uploads')?.select('*', { count: 'exact', head: true })?.gte('uploaded_at', last7DaysStart?.toISOString())?.lte('uploaded_at', lastLoginDate?.toISOString());
        
        const { count: previousCount } = await supabase?.from('team_stage_uploads')?.select('*', { count: 'exact', head: true })?.gte('uploaded_at', previous7DaysStart?.toISOString())?.lt('uploaded_at', last7DaysStart?.toISOString());
        
        currentValue = currentCount || 0;
        previousValue = previousCount || 0;
        break;
      }
      
      case 'cities': {
        // Count unique cities from user_profiles created in last 7 days
        const { data: currentData } = await supabase?.from('user_profiles')?.select('location')?.not('location', 'is', null)?.gte('created_at', last7DaysStart?.toISOString())?.lte('created_at', lastLoginDate?.toISOString());
        
        const { data: previousData } = await supabase?.from('user_profiles')?.select('location')?.not('location', 'is', null)?.gte('created_at', previous7DaysStart?.toISOString())?.lt('created_at', last7DaysStart?.toISOString());
        
        const currentCities = new Set(
          currentData?.map(p => p?.location?.split(',')[0]?.trim()).filter(Boolean)
        );
        const previousCities = new Set(
          previousData?.map(p => p?.location?.split(',')[0]?.trim()).filter(Boolean)
        );
        
        currentValue = currentCities?.size;
        previousValue = previousCities?.size;
        break;
      }
      
      default:
        throw new Error(`Unknown metric type: ${metric}`);
    }

    // Calculate percentage change
    let percentage = 0;
    let changeType = 'neutral';

    if (previousValue === 0 && currentValue > 0) {
      // New activity case
      return {
        current: currentValue,
        previous: previousValue,
        percentage: null,
        changeType: 'new',
        displayText: 'New activity'
      };
    } else if (previousValue === 0 && currentValue === 0) {
      // No change, both zero
      return {
        current: currentValue,
        previous: previousValue,
        percentage: 0,
        changeType: 'neutral',
        displayText: '0%'
      };
    } else {
      percentage = ((currentValue - previousValue) / previousValue) * 100;
      
      if (percentage > 0) {
        changeType = 'increase';
      } else if (percentage < 0) {
        changeType = 'decrease';
      } else {
        changeType = 'neutral';
      }
      
      const sign = percentage > 0 ? '+' : '';
      const displayText = `${sign}${percentage?.toFixed(1)}% in last 7 days`;
      
      return {
        current: currentValue,
        previous: previousValue,
        percentage: parseFloat(percentage?.toFixed(1)),
        changeType,
        displayText
      };
    }
  } catch (error) {
    console.error(`Error calculating 7-day change for ${metric}:`, error);
    return {
      current: 0,
      previous: 0,
      percentage: 0,
      changeType: 'neutral',
      displayText: '0%'
    };
  }
};

/**
 * Get comprehensive dashboard analytics with 7-day changes
 * @param {Date} adminLastLogin - Admin's last login timestamp
 * @returns {Promise<Object>} Object containing all analytics metrics with changes
 */
export const getDashboardAnalyticsWithChanges = async (adminLastLogin = new Date()) => {
  try {
    const [
      totalUsers,
      teamsFormed,
      deliverablesSubmitted,
      citiesParticipating,
      atRiskTeams,
      usersChange,
      teamsChange,
      deliverablesChange,
      citiesChange
    ] = await Promise.all([
      getTotalUsers(),
      getTotalTeamsFormed(),
      getTotalDeliverablesSubmitted(),
      getCitiesParticipating(),
      getTeamsAtRisk(),
      get7DayChange('users', adminLastLogin),
      get7DayChange('teams', adminLastLogin),
      get7DayChange('deliverables', adminLastLogin),
      get7DayChange('cities', adminLastLogin)
    ]);

    return {
      totalUsers,
      teamsFormed,
      deliverablesSubmitted,
      citiesParticipating,
      atRiskTeams,
      atRiskTeamsCount: atRiskTeams?.length || 0,
      changes: {
        users: usersChange,
        teams: teamsChange,
        deliverables: deliverablesChange,
        cities: citiesChange
      }
    };
  } catch (error) {
    console.error('Error fetching dashboard analytics with changes:', error);
    throw error;
  }
};

/**
 * Get comprehensive dashboard analytics
 * @returns {Promise<Object>} Object containing all analytics metrics
 */
export const getDashboardAnalytics = async () => {
  try {
    const [
      totalUsers,
      teamsFormed,
      deliverablesSubmitted,
      citiesParticipating,
      atRiskTeams
    ] = await Promise.all([
      getTotalUsers(),
      getTotalTeamsFormed(),
      getTotalDeliverablesSubmitted(),
      getCitiesParticipating(),
      getTeamsAtRisk()
    ]);

    return {
      totalUsers,
      teamsFormed,
      deliverablesSubmitted,
      citiesParticipating,
      atRiskTeams,
      atRiskTeamsCount: atRiskTeams?.length || 0
    };
  } catch (error) {
    console.error('Error fetching dashboard analytics:', error);
    throw error;
  }
};

/**
 * Get recent user signups for activity feed
 * @param {number} limit - Number of signups to retrieve
 * @returns {Promise<Array>} Array of recent signup activities
 */
export const getRecentSignups = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, full_name, role, created_at')
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return data?.map(user => ({
      id: user?.id,
      type: 'user_registration',
      title: 'New User Registration',
      description: `${user?.full_name} signed up as a ${user?.role === 'student' ? 'Student' : user?.role === 'city-official' ? 'City Official' : 'User'}`,
      user: user?.full_name,
      timestamp: new Date(user?.created_at)
    })) || [];
  } catch (error) {
    console.error('Error fetching recent signups:', error);
    throw error;
  }
};

export default {
  getTotalUsers,
  getTotalTeamsFormed,
  getTotalDeliverablesSubmitted,
  getCitiesParticipating,
  getTeamsAtRisk,
  getDashboardAnalytics,
  getDashboardAnalyticsWithChanges,
  get7DayChange,
  getRecentSignups
};
function getRecentActivity(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getRecentActivity is not implemented yet.', args);
  return null;
}

export { getRecentActivity };
function getTeamProgressData(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getTeamProgressData is not implemented yet.', args);
  return null;
}

export { getTeamProgressData };
function getUpcomingMeetings(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getUpcomingMeetings is not implemented yet.', args);
  return null;
}

export { getUpcomingMeetings };